import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { formErrors, revalidateLogic, useForm, useStore } from '@eureca/ui/forms';
import fetchClient from '@eureca/recruiter-api-sdk/client';
import { getKeycloak } from '@/auth/keycloak-instance';
import {
  CompanyCreatePage as CompanyCreatePageComponent,
  type CompanyCreateValues,
} from './CompanyCreatePage.component';
import { formatCnpj } from './cnpj';
import { companyFormSchema } from './company-form.schema';
import { validateLogoFile, type LogoValidationError } from './logo-upload';

interface BackendErrorBody {
  error?: string;
}

export default function CompanyCreatePage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoFileName, setLogoFileName] = useState<string | null>(null);
  const [logoPreviewUrl, setLogoPreviewUrl] = useState<string | null>(null);

  // Build a blob URL from the picked logo so the title avatar gets a live
  // preview without a round-trip to storage. Revoke on change/unmount to
  // avoid leaking blobs as the user picks → swaps → cancels.
  useEffect(() => {
    if (!logoFile) {
      setLogoPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(logoFile);
    setLogoPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [logoFile]);
  const [logoError, setLogoError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [duplicateField, setDuplicateField] = useState<'cnpj' | 'slug' | null>(null);

  const form = useForm({
    defaultValues: {
      name: '',
      cnpj: '',
      slug: '',
      website: '',
      primaryColor: '',
    } satisfies CompanyCreateValues,
    validationLogic: revalidateLogic(),
    validators: { onDynamic: companyFormSchema },
    onSubmit: async ({ value }) => {
      await submitCompany(value);
    },
  });

  const values = useStore(form.store, (state) => state.values);
  const isFormSubmitting = useStore(form.store, (state) => state.isSubmitting);
  const fieldErrors = formErrors(form);
  const errors: Record<string, string> = { ...fieldErrors };
  if (logoError) errors.logo = logoError;
  if (duplicateField === 'cnpj') errors.cnpj = 'CNPJ já cadastrado';
  if (duplicateField === 'slug') errors.slug = 'Slug já cadastrado';

  const createMutation = useMutation({
    mutationFn: async (form: FormData) => {
      // Multipart-thru-BFF — mirrors the candidate avatar pattern. The BFF
      // handles upload to storage, builds logoUrl, calls Core, deletes the
      // logo on Core failure, and writes the slug to the consultor's squad.
      const res = await fetchClient<{ id: string; slug: string }>({
        method: 'POST',
        url: '/companies',
        data: form as unknown,
      });
      if (res.error) {
        const body = res.error as { status: number; error?: BackendErrorBody } | undefined;
        throw new CreateCompanyError(body?.error?.error ?? 'create_failed', body?.status ?? 500);
      }
      return res.data;
    },
    onSuccess: async () => {
      // Force a fresh access token BEFORE invalidating the list cache. The
      // BFF derives the consultor's allowlist from the JWT `company_slug`
      // claim, which is recomputed when KC mints a new token from the squad
      // group's `company_slug` attribute (just updated by this create). If
      // we skip the refresh, the next /companies call still carries the old
      // claim and Core returns the pre-create result — the new company is
      // out of scope and the listing looks "stuck" until logout/F5.
      //
      // -1 means "ignore current token TTL, mint a new one now".
      try {
        await getKeycloak().updateToken(-1);
      } catch (err) {
        // Best-effort — if refresh fails (network blip, RP-cookie expired)
        // the user can still see the new company on the next natural refresh.

        console.warn('Could not refresh Keycloak token after company create', err);
      }
      // Invalidate every flavor of the companies list (table `page` + cards
      // `infinite`) so navigating back to /companies refetches fresh data
      // instead of showing the stale pre-create cache.
      void queryClient.invalidateQueries({ queryKey: ['recruiter', 'companies'] });
    },
  });

  const isSubmitting = isFormSubmitting || createMutation.isPending;

  async function submitCompany(value: CompanyCreateValues) {
    setSubmitError(null);
    setDuplicateField(null);
    setLogoError(null);

    if (!logoFile) {
      setLogoError('Envie uma imagem para a logo da empresa');
      return;
    }

    const form = new FormData();
    form.append('name', value.name);
    form.append('slug', value.slug);
    form.append('cnpj', value.cnpj);
    form.append('websiteUrl', value.website);
    form.append('primaryColor', value.primaryColor);
    form.append('logo', logoFile);

    try {
      await createMutation.mutateAsync(form);
      void navigate({ to: '/companies/create/success' });
    } catch (err) {
      handleSubmitError(err);
    }
  }

  function handleSubmitError(err: unknown) {
    if (!(err instanceof CreateCompanyError)) {
      setSubmitError('Falha ao criar a empresa. Tente novamente.');
      return;
    }
    if (err.errorKey === 'cnpj_duplicado') return setDuplicateField('cnpj');
    if (err.errorKey === 'slug_duplicado') return setDuplicateField('slug');
    if (err.errorKey === 'multiple_squads') {
      setSubmitError(
        'Você está vinculado a mais de um squad por isso não pode criar empresa. Contate um Administrador.'
      );
      return;
    }
    if (err.errorKey === 'kc_sync_failed') {
      setSubmitError(
        'Empresa criada, mas falhou ao vincular ao seu squad. Contate um Administrador.'
      );
      return;
    }
    if (err.errorKey === 'logo_too_large') {
      setLogoError('O arquivo deve ter no máximo 5 MB');
      return;
    }
    if (err.errorKey === 'invalid_logo_type') {
      setLogoError('Envie um arquivo .JPG ou .PNG');
      return;
    }
    if (err.errorKey === 'logo_required') {
      setLogoError('Envie uma imagem para a logo da empresa');
      return;
    }
    setSubmitError('Falha ao criar a empresa. Tente novamente.');
  }

  async function handleLogoChange(file: File | null) {
    setLogoError(null);
    if (!file) {
      setLogoFile(null);
      setLogoFileName(null);
      return;
    }
    const result = await validateLogoFile(file);
    if (!result.ok) {
      setLogoError(translateLogoError(result.error));
      setLogoFile(null);
      setLogoFileName(null);
      return;
    }
    setLogoFile(file);
    setLogoFileName(file.name);
  }

  return (
    <CompanyCreatePageComponent
      values={values}
      errors={errors}
      logoFileName={logoFileName}
      logoPreviewUrl={logoPreviewUrl}
      isSubmitting={isSubmitting}
      submitError={submitError}
      onFieldChange={(field, value) => {
        if (field === 'cnpj') {
          setDuplicateField(null);
          form.setFieldValue(field, formatCnpj(value));
          return;
        }
        if (field === 'slug') setDuplicateField(null);
        form.setFieldValue(field, value);
      }}
      onLogoChange={handleLogoChange}
      onSubmit={() => form.handleSubmit()}
    />
  );
}

class CreateCompanyError extends Error {
  readonly name = 'CreateCompanyError';
  constructor(
    readonly errorKey: string,
    readonly status: number
  ) {
    super(errorKey);
  }
}

function translateLogoError(err: LogoValidationError): string {
  return err.message;
}
